import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Eye, Pencil, Copy, Trash2, Download, Share2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, API, money } from "@/lib/api";

const STATUS = { draft: "bg-secondary text-foreground", sent: "bg-blue-100 text-blue-700", approved: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700" };

export default function AdminProposals() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/proposals").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const duplicate = async (id) => {
    try { const r = await api.post(`/proposals/${id}/duplicate`); toast.success("Duplicated"); navigate(`/admin/proposals/${r.data.id}/edit`); }
    catch { toast.error("Failed to duplicate"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this proposal? This cannot be undone.")) return;
    try { await api.delete(`/proposals/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };
  const share = (shareId) => {
    const url = `${window.location.origin}/proposal/${shareId}`;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied to clipboard");
  };

  const filtered = items.filter((p) => `${p.title} ${p.proposal_number} ${p.customer?.name || ""} ${p.customer?.company || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Proposals</h1>
          <p className="text-muted-foreground text-sm mt-1">{items.length} total</p>
        </div>
        <Link to="/admin/proposals/new" data-testid="new-proposal-btn" className="flex items-center gap-2 bg-foreground text-background px-5 py-2.5 text-[12px] tracking-[0.1em] uppercase hover:bg-onyx-wood hover:text-onyx-black transition-colors"><Plus size={15} /> New Proposal</Link>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="proposal-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search proposals…" className="w-full bg-card border border-border pl-10 pr-4 py-2.5 text-sm outline-none focus:border-foreground transition-colors" />
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] tracking-[0.12em] uppercase text-muted-foreground border-b border-border">
            <th className="text-left px-5 py-3 font-medium">Proposal</th>
            <th className="text-left px-5 py-3 font-medium">Customer</th>
            <th className="text-right px-5 py-3 font-medium">Total</th>
            <th className="text-left px-5 py-3 font-medium">Status</th>
            <th className="text-right px-5 py-3 font-medium">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-border">
            {filtered.map((p) => (
              <tr key={p.id} className="hover:bg-secondary/40 transition-colors" data-testid={`proposal-row-${p.id}`}>
                <td className="px-5 py-4"><div className="font-medium">{p.title}</div><div className="text-xs text-muted-foreground">{p.proposal_number} · {p.date}</div></td>
                <td className="px-5 py-4">{p.customer?.name || p.customer?.company || "—"}</td>
                <td className="px-5 py-4 text-right font-mono-num">{money(p.total)}</td>
                <td className="px-5 py-4"><span className={`text-[10px] tracking-wide uppercase px-2.5 py-1 ${STATUS[p.status] || STATUS.draft}`}>{p.status}</span></td>
                <td className="px-5 py-4">
                  <div className="flex items-center justify-end gap-1 text-muted-foreground">
                    <Link to={`/admin/proposals/${p.id}/preview`} title="Preview" data-testid={`preview-${p.id}`} className="p-2 hover:text-foreground hover:bg-secondary transition-colors"><Eye size={16} /></Link>
                    <Link to={`/admin/proposals/${p.id}/edit`} title="Edit" data-testid={`edit-${p.id}`} className="p-2 hover:text-foreground hover:bg-secondary transition-colors"><Pencil size={16} /></Link>
                    <button onClick={() => duplicate(p.id)} title="Duplicate" data-testid={`duplicate-${p.id}`} className="p-2 hover:text-foreground hover:bg-secondary transition-colors"><Copy size={16} /></button>
                    <a href={`${API}/proposals/${p.id}/pdf`} title="Download PDF" data-testid={`pdf-${p.id}`} className="p-2 hover:text-foreground hover:bg-secondary transition-colors"><Download size={16} /></a>
                    <button onClick={() => share(p.share_id)} title="Share link" data-testid={`share-${p.id}`} className="p-2 hover:text-foreground hover:bg-secondary transition-colors"><Share2 size={16} /></button>
                    <button onClick={() => remove(p.id)} title="Delete" data-testid={`delete-${p.id}`} className="p-2 hover:text-destructive hover:bg-secondary transition-colors"><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-muted-foreground">No proposals found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
